import { supabase } from '@/integrations/supabase/client';

const RESTRICTED_TABLES = new Set(['quiz_pageviews', 'shopify_orders', 'quiz_answers']);

async function fetchViaEdge<T>(
  table: string,
  selectColumns: string,
  filters: { column: string; operator: string; value: unknown }[] | undefined,
): Promise<T[]> {
  const dashboardKey = typeof window !== 'undefined' ? localStorage.getItem('dashboard_key') : null;
  if (!dashboardKey) return [];
  const PAGE_SIZE = 1000;
  let all: T[] = [];
  let from = 0;
  let hasMore = true;
  while (hasMore) {
    const { data, error } = await supabase.functions.invoke('dashboard-query', {
      body: { table, select: selectColumns, filters, range: [from, from + PAGE_SIZE - 1] },
      headers: { 'x-dashboard-key': dashboardKey },
    });
    if (error || !data?.data) break;
    const rows = data.data as T[];
    all = all.concat(rows);
    hasMore = rows.length === PAGE_SIZE;
    from += PAGE_SIZE;
  }
  return all;
}

export async function dashboardSelect<T>(
  table: string,
  selectColumns: string,
  options: {
    filters?: { column: string; operator: string; value: unknown }[];
    order?: { column: string; ascending?: boolean };
    limit?: number;
  } = {},
): Promise<T[]> {
  const dashboardKey = typeof window !== 'undefined' ? localStorage.getItem('dashboard_key') : null;
  if (!dashboardKey) return [];
  const { data, error } = await supabase.functions.invoke('dashboard-query', {
    body: {
      table,
      select: selectColumns,
      filters: options.filters,
      order: options.order,
      limit: options.limit,
    },
    headers: { 'x-dashboard-key': dashboardKey },
  });
  if (error || !data?.data) return [];
  return data.data as T[];
}

export async function dashboardMutate(
  table: string,
  id: string,
  values: Record<string, unknown>,
): Promise<{ success: boolean; error?: string }> {
  const dashboardKey = typeof window !== 'undefined' ? localStorage.getItem('dashboard_key') : null;
  if (!dashboardKey) return { success: false, error: 'Not authenticated' };
  const { data, error } = await supabase.functions.invoke('dashboard-mutate', {
    body: { table, id, values },
    headers: { 'x-dashboard-key': dashboardKey },
  });
  if (error) return { success: false, error: error.message };
  return { success: !!data?.success };
}

export async function dashboardCount(
  table: string,
  filters: { column: string; operator: string; value: unknown }[] = [],
): Promise<number> {
  const dashboardKey = typeof window !== 'undefined' ? localStorage.getItem('dashboard_key') : null;
  if (!dashboardKey) return 0;
  const { data, error } = await supabase.functions.invoke('dashboard-query', {
    body: { table, select: '*', filters, count: 'exact', head: true },
    headers: { 'x-dashboard-key': dashboardKey },
  });
  if (error) return 0;
  return (data?.count as number) || 0;
}

/**
 * Fetch ALL rows from a Supabase table, paginating in batches of 1000.
 * Works around the default 1000-row limit.
 */
export async function fetchAllRows<T = Record<string, unknown>>(
  table: string,
  selectColumns: string,
  filters?: { column: string; operator: string; value: unknown }[]
): Promise<T[]> {
  if (RESTRICTED_TABLES.has(table)) {
    return fetchViaEdge<T>(table, selectColumns, filters);
  }
  const PAGE_SIZE = 1000;
  let allData: T[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    let query = (supabase as any).from(table).select(selectColumns).range(from, from + PAGE_SIZE - 1);

    if (filters) {
      for (const f of filters) {
        if (f.operator === 'eq') query = query.eq(f.column, f.value);
        else if (f.operator === 'not.is') query = query.not(f.column, 'is', f.value);
        else if (f.operator === 'is') query = query.is(f.column, f.value as null);
        else if (f.operator === 'gte') query = query.gte(f.column, f.value);
        else if (f.operator === 'lte') query = query.lte(f.column, f.value);
        else if (f.operator === 'lt') query = query.lt(f.column, f.value);
      }
    }

    const { data, error } = await query;
    if (error || !data) break;

    allData = allData.concat(data as T[]);
    hasMore = data.length === PAGE_SIZE;
    from += PAGE_SIZE;
  }

  return allData;
}
